import { apiFetch } from '@/lib/api/client';
import type {
  AssignTeacherInput,
  ClassStudentView,
  ClassTeacherView,
  ClassView,
  CreateClassInput,
  EnrollStudentInput,
  TeacherRosterView,
  UpdateClassInput,
} from '@/features/classes/types';
import type { PagedData } from '@/lib/api/types';

/** `GET /classes`. Tenant-scoped (ADR-026 Addendum, AO-2). Optional `learningSpaceId` filter. */
export async function listClasses(
  page = 1,
  limit = 20,
  learningSpaceId?: string,
): Promise<PagedData<ClassView>> {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (learningSpaceId !== undefined) {
    query.set('learningSpaceId', learningSpaceId);
  }
  return apiFetch<PagedData<ClassView>>(`/classes?${query.toString()}`, { tenantScoped: true });
}

/** `GET /classes/:id`. Tenant-scoped. */
export async function getClass(id: string): Promise<ClassView> {
  return apiFetch<ClassView>(`/classes/${id}`, { tenantScoped: true });
}

/** `POST /classes`. Tenant-scoped; requires `classes:manage`. */
export async function createClass(input: CreateClassInput): Promise<ClassView> {
  return apiFetch<ClassView>('/classes', { method: 'POST', body: input, tenantScoped: true });
}

/** `PATCH /classes/:id`. Tenant-scoped; requires `classes:manage`. */
export async function updateClass(id: string, input: UpdateClassInput): Promise<ClassView> {
  return apiFetch<ClassView>(`/classes/${id}`, {
    method: 'PATCH',
    body: input,
    tenantScoped: true,
  });
}

/** `DELETE /classes/:id`. Tenant-scoped; requires `classes:manage`. Soft deactivation, no reactivation endpoint. */
export async function deactivateClass(id: string): Promise<void> {
  await apiFetch<unknown>(`/classes/${id}`, { method: 'DELETE', tenantScoped: true });
}

/**
 * `GET /teachers`. Tenant-scoped; requires `teachers:read`. The organization's
 * roster of users holding an active Teacher membership - there is no teacher
 * table (ADR-020) - used to populate the assignment picker below.
 */
export async function listTeacherRoster(
  page = 1,
  limit = 100,
): Promise<PagedData<TeacherRosterView>> {
  return apiFetch<PagedData<TeacherRosterView>>(
    `/teachers?page=${String(page)}&limit=${String(limit)}`,
    { tenantScoped: true },
  );
}

/** `GET /classes/:id/teachers`. Tenant-scoped; requires `teachers:read`. ACTIVE assignments only. */
export async function listClassTeachers(classId: string): Promise<readonly ClassTeacherView[]> {
  return apiFetch<readonly ClassTeacherView[]>(`/classes/${classId}/teachers`, {
    tenantScoped: true,
  });
}

/**
 * `POST /classes/:id/teachers`. Tenant-scoped; requires `teachers:manage`.
 * Rejects a `teacherId` that does not hold an active Teacher membership in
 * this organization (`UnprocessableAssignmentError`, audited server-side).
 */
export async function assignTeacher(
  classId: string,
  input: AssignTeacherInput,
): Promise<ClassTeacherView> {
  return apiFetch<ClassTeacherView>(`/classes/${classId}/teachers`, {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/** `DELETE /classes/:id/teachers/:teacherId`. Tenant-scoped; requires `teachers:manage`. Stamps `unassignedAt`, preserves history. */
export async function removeTeacher(classId: string, teacherId: string): Promise<void> {
  await apiFetch<unknown>(`/classes/${classId}/teachers/${teacherId}`, {
    method: 'DELETE',
    tenantScoped: true,
  });
}

/** `GET /classes/:id/students`. Tenant-scoped; requires `students:read`. ACTIVE enrollments only. */
export async function listClassStudents(classId: string): Promise<readonly ClassStudentView[]> {
  return apiFetch<readonly ClassStudentView[]>(`/classes/${classId}/students`, {
    tenantScoped: true,
  });
}

/**
 * `POST /classes/:id/students`. Tenant-scoped; requires `students:manage`.
 * Rejects a `studentId` whose user does not hold an active Student
 * membership in this organization.
 */
export async function enrollStudent(
  classId: string,
  input: EnrollStudentInput,
): Promise<ClassStudentView> {
  return apiFetch<ClassStudentView>(`/classes/${classId}/students`, {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/** `DELETE /classes/:id/students/:studentId`. Tenant-scoped; requires `students:manage`. Stamps `endedAt`, preserves history. */
export async function removeStudent(classId: string, studentId: string): Promise<void> {
  await apiFetch<unknown>(`/classes/${classId}/students/${studentId}`, {
    method: 'DELETE',
    tenantScoped: true,
  });
}
